import { useMutation, useQueryClient } from 'react-query'
import { createCustomer } from 'services/cm-api/create-customer'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router'

/**
 * CMAPI's error handler answers with `{ success: false, error: { name, message } }`.
 * The message is written for the operator ("A customer for DK 87552411 already
 * exists (id 3012)", "Failed to create user. Email already exists"), so show it
 * instead of the old catch-all 'Create user failed' — that snackbar hid both the
 * reason and the fact that a company had been created anyway (ENG2-2325).
 */
const getServerErrorMessage = (err: unknown): string => {
  const message = (
    err as { response?: { data?: { error?: { message?: string } } } }
  )?.response?.data?.error?.message

  return message || 'Failed to create customer'
}

export function useCreateCustomer() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(createCustomer, {
    onSuccess: async ({ data }) => {
      queryClient.invalidateQueries('getCustomers')

      enqueueSnackbar('Customer created', { variant: 'success' })
      await queryClient.refetchQueries('getCustomers')
      navigate(`/customer/${data.customer.id}`)
    },
    onError: (err) => {
      enqueueSnackbar(getServerErrorMessage(err), { variant: 'error' })
      console.error('createCustomer failed: ', err)
    },
  })
}
